import * as BABYLON from 'babylonjs';
import { ToolType, WorldModel } from "../models/WorldModel";
import { BeingController } from "./BeingController";
import { BeastController } from "./BeastController";
import { Surroundings } from '../models/Surroundings';
import { Being } from '../models/Being';
import { Beast } from '../models/Beast';
import { Direction, AI, landThresholds as thresholds, SOCIAL, BEAST } from '../models/constants';
import { PubSub } from '../PubSub';
import type { GameEvents } from '../events';
import { DeadState } from './states/being/DeadState';

const CONFIG = {
    BRUSH_RADIUS: 10,
    BRUSH_STRENGTH: 2.0,
    SMOOTH_FACTOR: 0.35,
    MAX_HEIGHT: 15.0,
    MIN_HEIGHT: -3.0
};

/** World-space spread of a newborn's position around its parent. */
const BIRTH_SCATTER = 2.0;

export class WorldController {
    private worldModel: WorldModel;
    private pubsub: PubSub<GameEvents>;
    private beingController: BeingController;
    private beastController: BeastController | null = null;
    private pendingBirths: Being[] = [];

    /**
     * @param worldModel - The world data model to read and mutate.
     * @param pubsub - The event bus. Subscribes to `world:spawn-beings` to generate
     *   beings, add them to the model, and publish `world:beings-spawned`.
     */
    constructor(worldModel: WorldModel, pubsub: PubSub<GameEvents>) {
        this.worldModel = worldModel;
        this.pubsub = pubsub;
        this.beingController = new BeingController({ states: worldModel.level.beingStates });

        pubsub.subscribe('world:spawn-beings', (count: number) => {
            const halfSize = this.worldModel.terrainSize / 2;
            const beings: Being[] = [];
            for (let i = 0; i < count; i++) {
                const x = (Math.random() * this.worldModel.terrainSize) - halfSize;
                const z = (Math.random() * this.worldModel.terrainSize) - halfSize;
                beings.push(new Being(x, z));
            }
            this.worldModel.addBeings(beings);
            this.pubsub.publish('world:beings-spawned', beings);
        });

        // Births are buffered rather than added immediately: `world:being-born` is
        // published from inside the being tick loop, and pushing straight into the
        // model's array would tick the newborn in the same simulation step.
        pubsub.subscribe('world:being-born', ({ x, z }) => {
            const offsetX = (Math.random() - 0.5) * BIRTH_SCATTER;
            const offsetZ = (Math.random() - 0.5) * BIRTH_SCATTER;
            this.pendingBirths.push(new Being(x + offsetX, z + offsetZ));
        });

        pubsub.subscribe('world:tool-changed', (toolName: string) => {
            switch(toolName) {
                case 'raise': this.worldModel.currentTool = ToolType.Raise; break;
                case 'lower': this.worldModel.currentTool = ToolType.Lower; break;
                case 'flatten': this.worldModel.currentTool = ToolType.Flatten; break;
                case 'spawn-beast': this.worldModel.currentTool = ToolType.SpawnBeast; break;
            }
        });

        // Rebuild the being state machine when a new level starts, so the controller
        // picks up that level's beingStates (e.g. Level 2's Socializing/Hunting states
        // and its attraction-enabled ExploringState). Without this the controller would
        // keep running the level it was constructed with.
        pubsub.subscribe('level:started', () => {
            this.beingController = new BeingController({ states: this.worldModel.level.beingStates });
        });
    }

    /**
     * Raises terrain within the brush radius around `center` using a Gaussian falloff,
     * clamped to `MAX_HEIGHT`. Publishes `world:ground-updated`, `world:brush-update`,
     * and `world:beings-update` if any vertex was modified.
     */
    public raiseLand(center: BABYLON.Vector3): void {
        const terrainHeights = this.worldModel.terrainHeights;
        const subdivisions = this.worldModel.subdivisions;
        const terrainSize = this.worldModel.terrainSize;

        let modified = false;
        const radiusSq = CONFIG.BRUSH_RADIUS * CONFIG.BRUSH_RADIUS;

        for (let i = 0; i < terrainHeights.length; i++) {
            const row = Math.floor(i / (subdivisions + 1));
            const col = i % (subdivisions + 1);

            const x = (col / subdivisions) * terrainSize - (terrainSize / 2);
            const z = (terrainSize / 2) - (row / subdivisions) * terrainSize;

            const dx = x - center.x;
            const dz = z - center.z;
            const distSq = dx * dx + dz * dz;

            if (distSq < radiusSq) {
                const falloff = Math.exp(-distSq / (CONFIG.BRUSH_RADIUS * 2));
                terrainHeights[i] += CONFIG.BRUSH_STRENGTH * falloff;
                if (terrainHeights[i] > CONFIG.MAX_HEIGHT) {
                    terrainHeights[i] = CONFIG.MAX_HEIGHT;
                }
                modified = true;
            }
        }

        if (modified) {
            this.worldModel.updateTerrainHeights(terrainHeights);
            this.pubsub.publish('world:ground-updated', center);
            this.pubsub.publish('world:brush-update', { center, radius: CONFIG.BRUSH_RADIUS });
            this.pubsub.publish('world:beings-update', { beings: this.worldModel.beings, instant: true });
        }
    }

    /**
     * Lowers terrain within the brush radius around `center` using a Gaussian falloff,
     * clamped to `MIN_HEIGHT`. Publishes `world:ground-updated`, `world:brush-update`,
     * and `world:beings-update` if any vertex was modified.
     */
    public lowerLand(center: BABYLON.Vector3): void {
        const terrainHeights = this.worldModel.terrainHeights;
        const subdivisions = this.worldModel.subdivisions;
        const terrainSize = this.worldModel.terrainSize;

        let modified = false;
        const radiusSq = CONFIG.BRUSH_RADIUS * CONFIG.BRUSH_RADIUS;

        for (let i = 0; i < terrainHeights.length; i++) {
            const row = Math.floor(i / (subdivisions + 1));
            const col = i % (subdivisions + 1);

            const x = (col / subdivisions) * terrainSize - (terrainSize / 2);
            const z = (terrainSize / 2) - (row / subdivisions) * terrainSize;

            const dx = x - center.x;
            const dz = z - center.z;
            const distSq = dx * dx + dz * dz;

            if (distSq < radiusSq) {
                const falloff = Math.exp(-distSq / (CONFIG.BRUSH_RADIUS * 2));
                terrainHeights[i] -= CONFIG.BRUSH_STRENGTH * falloff;
                if (terrainHeights[i] < CONFIG.MIN_HEIGHT) {
                    terrainHeights[i] = CONFIG.MIN_HEIGHT;
                }
                modified = true;
            }
        }

        if (modified) {
            this.worldModel.updateTerrainHeights(terrainHeights);
            this.pubsub.publish('world:ground-updated', center);
            this.pubsub.publish('world:brush-update', { center, radius: CONFIG.BRUSH_RADIUS });
            this.pubsub.publish('world:beings-update', { beings: this.worldModel.beings, instant: true });
        }
    }

    /**
     * Softens terrain within the brush radius around `center` by nudging each vertex
     * toward the average height of all vertices in the brush, weighted by a Gaussian
     * falloff and `SMOOTH_FACTOR`.
     */
    public flattenLand(center: BABYLON.Vector3): void {
        const terrainHeights = this.worldModel.terrainHeights;
        const subdivisions = this.worldModel.subdivisions;
        const terrainSize = this.worldModel.terrainSize;

        const radiusSq = CONFIG.BRUSH_RADIUS * CONFIG.BRUSH_RADIUS;

        let sum = 0;
        let count = 0;
        const inBrush: { index: number; falloff: number }[] = [];

        for (let i = 0; i < terrainHeights.length; i++) {
            const row = Math.floor(i / (subdivisions + 1));
            const col = i % (subdivisions + 1);

            const x = (col / subdivisions) * terrainSize - (terrainSize / 2);
            const z = (terrainSize / 2) - (row / subdivisions) * terrainSize;

            const dx = x - center.x;
            const dz = z - center.z;
            const distSq = dx * dx + dz * dz;

            if (distSq < radiusSq) {
                const falloff = Math.exp(-distSq / (CONFIG.BRUSH_RADIUS * 2));
                sum += terrainHeights[i];
                count += 1;
                inBrush.push({ index: i, falloff });
            }
        }

        if (count === 0) return;

        const average = sum / count;

        for (const { index, falloff } of inBrush) {
            const delta = (average - terrainHeights[index]) * CONFIG.SMOOTH_FACTOR * falloff;
            terrainHeights[index] += delta;
        }

        this.worldModel.updateTerrainHeights(terrainHeights);
        this.pubsub.publish('world:ground-updated', center);
        this.pubsub.publish('world:brush-update', { center, radius: CONFIG.BRUSH_RADIUS });
        this.pubsub.publish('world:beings-update', { beings: this.worldModel.beings, instant: true });
    }

    /**
     * Advances one simulation step for all beings and beasts.
     * Order: tick beings (a hunting being's lethal `beast.damage()` call publishes
     * `world:beast-killed` itself) → tick beasts → remove dead beasts → publish.
     */
    public tick(): void {
        const beings = this.worldModel.beings;
        const beasts = this.worldModel.beasts;
        const terrainSize = this.worldModel.terrainSize;
        const halfSize = terrainSize / 2;

        for (const being of beings) {
            const surroundings = this.getSurroundings(being);
            this.beingController.tick(being, surroundings);
            being.x = Math.max(-halfSize, Math.min(halfSize, being.x));
            being.z = Math.max(-halfSize, Math.min(halfSize, being.z));
        }

        this.flushBirths();

        if (this.beastController) {
            for (const beast of beasts) {
                const surroundings = this.getSurroundings(beast);
                this.beastController.tick(beast, surroundings);
                beast.x = Math.max(-halfSize, Math.min(halfSize, beast.x));
                beast.z = Math.max(-halfSize, Math.min(halfSize, beast.z));
            }
            this.worldModel.removeDeadBeasts();
        }

        // Read the beast list back off the model rather than using the local const:
        // `removeDeadBeasts()` replaces the array, so the captured reference is stale
        // and would hand the level beasts that have already been swept.
        this.worldModel.level.tick(beings, this.worldModel.beasts);
        this.pubsub.publish('world:beings-update', { beings });
        this.pubsub.publish('world:beasts-update', { beasts: this.worldModel.beasts });

        if (!this.worldModel.gameOverFired && beings.length > 0 && beings.every(b => b.state === DeadState.stateName)) {
            this.worldModel.gameOverFired = true;
            this.pubsub.publish('game:over');
        }
    }

    /**
     * Adds any beings born during this tick to the world and announces them, so the
     * view builds their meshes. They are ticked from the next simulation step onward.
     */
    private flushBirths(): void {
        if (this.pendingBirths.length === 0) return;

        const newborns = this.pendingBirths;
        this.pendingBirths = [];
        this.worldModel.addBeings(newborns);
        this.pubsub.publish('world:beings-spawned', newborns);
    }

    /**
     * Scatters `count` Beasts at random positions across the map and publishes
     * `world:beasts-spawned` once for the whole batch. Beasts spawn with immunity,
     * so any that land in water or on a mountain can flee to safe ground first.
     * Called by a level's `begin()` rather than by a player tool.
     */
    public spawnBeasts(count: number): void {
        const halfSize = this.worldModel.terrainSize / 2;
        const beasts: Beast[] = [];
        for (let i = 0; i < count; i++) {
            const x = (Math.random() * this.worldModel.terrainSize) - halfSize;
            const z = (Math.random() * this.worldModel.terrainSize) - halfSize;
            beasts.push(new Beast(x, z, this.pubsub));
        }
        this.worldModel.addBeasts(beasts);
        this.pubsub.publish('world:beasts-spawned', beasts);
    }

    /**
     * Spawns a Beast at the given world position and publishes `world:beasts-spawned`.
     */
    public spawnBeastAt(position: BABYLON.Vector3): void {
        const beast = new Beast(position.x, position.z, this.pubsub);
        this.worldModel.addBeasts([beast]);
        this.pubsub.publish('world:beasts-spawned', [beast]);
    }

    /**
     * Registers the BeastController to use for beast ticking. Called when a level
     * that includes beasts (Level 2+) is started.
     *
     * By design the WorldController remains the single simulation engine: it ticks
     * beings and beasts in one place so cross-entity ordering (beings act → beasts
     * react → remove dead) lives together and stays symmetric with the BeingController.
     * Beast-specific behaviour (state ticking, health decay) is owned by BeastController;
     * kill attribution lives in `Beast.damage()`. The coupling here is just registration
     * + dispatch.
     */
    public setBeastController(controller: BeastController): void {
        this.beastController = controller;
    }

    // Grid offsets per direction. Row increases as world-Z decreases (row = 50 - z),
    // so North (dz=-1) maps to drow=+1.
    private static readonly DIR_TO_GRID = new Map<Direction, { drow: number; dcol: number }>([
        [Direction.North,     { drow: +1, dcol:  0 }],
        [Direction.Northeast, { drow: +1, dcol: +1 }],
        [Direction.East,      { drow:  0, dcol: +1 }],
        [Direction.Southeast, { drow: -1, dcol: +1 }],
        [Direction.South,     { drow: -1, dcol:  0 }],
        [Direction.Southwest, { drow: -1, dcol: -1 }],
        [Direction.West,      { drow:  0, dcol: -1 }],
        [Direction.Northwest, { drow: +1, dcol: -1 }],
    ]);

    /**
     * Samples the local environment around an entity (Being or Beast): terrain
     * altitude, per-direction sight lines and edge gradients, nearby beings, and
     * nearby beasts. Serves both populations — beings use `nearbyBeasts` to hunt and
     * `nearbyBeings` to socialise; beasts use `nearbyBeings` to detect and flee
     * hunters. The same `nearbyBeings`/`nearbyBeasts` sampling feeds both.
     */
    private getSurroundings(entity: Being | Beast): Surroundings {
        const terrainSize = this.worldModel.terrainSize;
        const subdivisions = this.worldModel.subdivisions;

        const col = Math.round(((entity.x + terrainSize / 2) / terrainSize) * subdivisions);
        const row = Math.round(((terrainSize / 2 - entity.z) / terrainSize) * subdivisions);

        const terrainHeights = this.worldModel.terrainHeights;
        const altitude = terrainHeights[row * (subdivisions + 1) + col] ?? 0;

        const currentEdgeDist = Math.min(row, subdivisions - row, col, subdivisions - col);

        const sightLines = new Map<Direction, (number | null)[]>();
        const edgeDelta = new Map<Direction, number>();
        for (const [dir, { drow, dcol }] of WorldController.DIR_TO_GRID) {
            const line: (number | null)[] = [];
            for (let d = 1; d <= AI.SIGHT_DEPTH; d++) {
                const r = row + drow * d;
                const c = col + dcol * d;
                const inBounds = r >= 0 && r <= subdivisions && c >= 0 && c <= subdivisions;
                line.push(inBounds ? (terrainHeights[r * (subdivisions + 1) + c] ?? 0) : null);
            }
            sightLines.set(dir, line);

            const r1 = row + drow;
            const c1 = col + dcol;
            const firstStepInBounds = r1 >= 0 && r1 <= subdivisions && c1 >= 0 && c1 <= subdivisions;
            const newEdgeDist = firstStepInBounds
                ? Math.min(r1, subdivisions - r1, c1, subdivisions - c1)
                : -1; // off-map: treat as deeper "outside" than the edge itself
            edgeDelta.set(dir, newEdgeDist - currentEdgeDist);
        }

        const nearbyBeings = this.worldModel.beings.filter(other => {
            if (other === entity) return false;
            const dx = other.x - entity.x;
            const dz = other.z - entity.z;
            return dx * dx + dz * dz < SOCIAL.SENSE_RADIUS_SQ;
        });

        const nearbyBeasts = this.worldModel.beasts.filter(beast => {
            if (beast === entity) return false;
            const dx = beast.x - entity.x;
            const dz = beast.z - entity.z;
            return dx * dx + dz * dz < BEAST.DETECTION_RADIUS_SQ;
        });

        return new Surroundings(altitude, sightLines, nearbyBeings, edgeDelta, currentEdgeDist, nearbyBeasts);
    }

    /**
     * Procedurally generates terrain by repeatedly selecting two random edge points and a midpoint
     * control point to form a quadratic Bézier curve across the map. The curve becomes
     * either a mountain range or a valley, with Gaussian brush strokes applied along it.
     * Brush centres are jittered perpendicular to the curve for a natural silhouette.
     * Publishes `world:ground-updated` once when finished.
     */
    public generateTerrain(): void {
        for (let fault = 0; fault < 6; fault++) {
            const halfSize = this.worldModel.terrainSize / 2;
            const terrainHeights = this.worldModel.terrainHeights;
            const subdivisions = this.worldModel.subdivisions;
            const terrainSize = this.worldModel.terrainSize;

            // Two random points on the map edge, one random interior control point
            const p0 = this.randomEdgePoint(halfSize);
            const p2 = this.randomEdgePoint(halfSize);
            const p1x = (Math.random() - 0.5) * halfSize;
            const p1z = (Math.random() - 0.5) * halfSize;

            const faultLineHeights = [CONFIG.MIN_HEIGHT, CONFIG.MAX_HEIGHT, thresholds.sea, thresholds.highland, thresholds.mountain, thresholds.snow];

            // Random target height; sign determines mountain vs valley
            const targetHeight = faultLineHeights[Math.floor(Math.random() * faultLineHeights.length)];
            const direction = Math.sign(targetHeight) || 1;
            // Scale brush strength so a gentler height choice produces gentler terrain
            const maxExtent = direction > 0 ? CONFIG.MAX_HEIGHT : Math.abs(CONFIG.MIN_HEIGHT);
            const strengthScale = Math.abs(targetHeight) / maxExtent;

            const NUM_SAMPLES = 60;
            const NOISE_AMPLITUDE = halfSize * 0.15;
            const radiusSq = CONFIG.BRUSH_RADIUS * CONFIG.BRUSH_RADIUS;

            for (let s = 0; s <= NUM_SAMPLES; s++) {
                const t = s / NUM_SAMPLES;
                const mt = 1 - t;

                // Quadratic Bézier position
                let cx = mt * mt * p0.x + 2 * mt * t * p1x + t * t * p2.x;
                let cz = mt * mt * p0.z + 2 * mt * t * p1z + t * t * p2.z;

                // Bézier tangent → perpendicular unit vector for jitter
                const tx = 2 * mt * (p1x - p0.x) + 2 * t * (p2.x - p1x);
                const tz = 2 * mt * (p1z - p0.z) + 2 * t * (p2.z - p1z);
                const tLen = Math.sqrt(tx * tx + tz * tz) || 1;
                const perpX = -tz / tLen;
                const perpZ =  tx / tLen;

                const noise = (Math.random() - 0.5) * 2 * NOISE_AMPLITUDE;
                cx += perpX * noise;
                cz += perpZ * noise;

                // Apply a single Gaussian brush stroke at this centre, directly into the
                // heights array (no events fired per stroke — one publish at the end)
                for (let i = 0; i < terrainHeights.length; i++) {
                    const row = Math.floor(i / (subdivisions + 1));
                    const col = i % (subdivisions + 1);

                    const x = (col / subdivisions) * terrainSize - (terrainSize / 2);
                    const z = (terrainSize / 2) - (row / subdivisions) * terrainSize;

                    const dx = x - cx;
                    const dz = z - cz;
                    const distSq = dx * dx + dz * dz;

                    if (distSq < radiusSq) {
                        const falloff = Math.exp(-distSq / (CONFIG.BRUSH_RADIUS * 2));
                        terrainHeights[i] += direction * CONFIG.BRUSH_STRENGTH * strengthScale * falloff;
                        terrainHeights[i] = Math.max(CONFIG.MIN_HEIGHT, Math.min(CONFIG.MAX_HEIGHT, terrainHeights[i]));
                    }
                }
            }
            this.worldModel.updateTerrainHeights(terrainHeights);
        }

        this.pubsub.publish('world:ground-updated', BABYLON.Vector3.Zero());
    }

    /** Returns a random point on the perimeter of the square map. */
    private randomEdgePoint(halfSize: number): { x: number; z: number } {
        const pos = (Math.random() - 0.5) * 2 * halfSize;
        switch (Math.floor(Math.random() * 4)) {
            case 0: return { x: pos, z: -halfSize }; // south
            case 1: return { x: pos, z:  halfSize }; // north
            case 2: return { x: -halfSize, z: pos }; // west
            default: return { x:  halfSize, z: pos }; // east
        }
    }

    /** Returns the terrain height at the given grid (row, col), or null if off the map. */
    private getTerrainHeight(row: number, col: number): number | null {
        const subdivisions = this.worldModel.subdivisions;
        if (row < 0 || row > subdivisions || col < 0 || col > subdivisions) return null;
        const stride = subdivisions + 1;
        return this.worldModel.terrainHeights[row * stride + col];
    }
}
